// Pure, locale-independent date parsing helpers (ported from legacy `composables/format.js`
// L561–585). No Intl, no Vue, no store — calendar plumbing only.

/** A value we can coerce into a `Date`: an ISO/loose string, an epoch number, or a `Date`. */
export type LooseDate = string | number | Date

const SPLIT = /[.\-/]/

/**
 * Parse a loose date value into a `Date`. Strings have a leading `T00:00:00`
 * midnight marker stripped first so a date-only value keeps day semantics.
 * (Legacy `getFormattedDate`.)
 */
export function parseLooseDate(value: LooseDate): Date {
  if (typeof value !== 'string') {
    return new Date(value)
  }
  return new Date(value.replace('T00:00:00', ''))
}

/**
 * Convert a date-only string to the UTC-midnight epoch of that calendar day, so a
 * date-only value never shifts a day across timezones. Accepts `YYYY-MM-DD`
 * (optionally suffixed by `T00:00:00` or ` 00:00:00`) and `DD-MM-YYYY` / `DD.MM.YYYY`
 * / `DD/MM/YYYY`. (Legacy `toUtcDay`.)
 *
 * Sharpened: legacy `str.includes(' 00:00:00', '')` passed `''` as a `fromIndex`
 * (which coerces to `0`) — it "worked" only by accident. The stray second argument
 * is removed here.
 */
export function toUtcDay(str: string | null | undefined): number | null {
  if (!str) return null

  // `YYYY-MM-DD…` shape: the 5th char is the year/month separator.
  if (str[4] === '-') {
    const stripped = str.includes(' 00:00:00')
      ? str.replace(' 00:00:00', '')
      : str.replace('T00:00:00', '')
    const [y, m, d] = stripped.split('-')
    return Date.UTC(Number(y), Number(m) - 1, Number(d))
  }

  // `DD<sep>MM<sep>YYYY` shape.
  const [d, m, y] = str.replace('T00:00:00', '').split(SPLIT)
  return Date.UTC(Number(y), Number(m) - 1, Number(d))
}
